// =============================================================================
// BALANCE TUNING — src/balance.ts
// This is the single file for adjusting game balance.
// Edit values here to tune enemy scaling, unit costs, upgrade chains,
// prestige pacing, and achievements — without touching any game logic.
// =============================================================================


// ---- CHAIN UPGRADE COSTS -------------------------------------------------------
// Cost formula: firstStepCost × CHAIN_COST_SCALE^stepIndex × chainCostMult × CHAIN_COST_GLOBAL_MULT
//
// To make ALL chains cheaper/expensive: change CHAIN_COST_GLOBAL_MULT (0.8 = -20%)
// To make ONE chain cheaper/expensive: change that chain's firstStepCost or costMult
// To override a specific step: add costOverride to that step in config.ts
//
export const CHAIN_COST_SCALE = 2.6;        // Cost multiplier between consecutive steps
export const CHAIN_COST_GLOBAL_MULT = 1.0;  // Global multiplier on all chain costs

// Per-chain configuration
// powerMult  — scales ALL damageMultBonus values. 0.8 = every step gives 20% less damage.
//              Does NOT affect utility steps (noDamage: true) or non-damage chains.
// firstStepCost — anchor cost for step 0; all subsequent costs scale from this.
// costMult   — additional per-chain cost multiplier (stacks with CHAIN_COST_GLOBAL_MULT).
export interface ChainCfg {
  powerMult: number;
  firstStepCost: number;
  costMult?: number; // defaults to 1.0
}

export const CHAIN_CONFIG: Record<string, ChainCfg> = {
  //                         powerMult  firstStepCost  (costMult optional)
  rock_thrower: { powerMult: 1.0, firstStepCost: 150 },
  builder: { powerMult: 1.0, firstStepCost: 400 },
  archer: { powerMult: 1.0, firstStepCost: 350 },
  club_swinger: { powerMult: 1.0, firstStepCost: 800 },
  sling_thrower: { powerMult: 1.0, firstStepCost: 2500 },
  spear_thrower: { powerMult: 1.0, firstStepCost: 4500 },
  longbow_archer: { powerMult: 1.0, firstStepCost: 11000 },
  firefly_nest: { powerMult: 1.0, firstStepCost: 32000 },
  crossbow: { powerMult: 1.0, firstStepCost: 45000 },
  beehive_thrower: { powerMult: 1.0, firstStepCost: 55000 },
  crow_trainer: { powerMult: 1.0, firstStepCost: 75000 },
  druid: { powerMult: 1.0, firstStepCost: 95000 },
  trebuchet: { powerMult: 1.0, firstStepCost: 115000 },
  lightning_wizard: { powerMult: 1.0, firstStepCost: 200000 },
  boomerang_thrower: { powerMult: 1.0, firstStepCost: 300000 },
  // finger chain uses explicit costs (flat/mult damage not scaled by powerMult)
};


// ---- ENEMY SCALING -------------------------------------------------------------
// Enemies grow exponentially each wave:
//   HP     = baseHp     × (1 + hpGrowth)^wave
//   Damage = baseDmg    × (1 + dmgGrowth)^wave
//   Gold   = baseGold   × (1 + goldGrowth)^wave
//
// Increase baseHp to make all enemies tankier at wave 1.
// Increase hpGrowth to make late-game enemies scale harder (higher % per wave).
//
export const ENEMY_SCALING = {
  baseHp: 50,
  baseDmg: 4,
  baseGold: 12,
  hpGrowthPerWave: 0.13,  // +12% HP per wave
  dmgGrowthPerWave: 0.11,  // +10% damage per wave
  goldGrowthPerWave: 0.09,  // +9% gold per wave
};


// ---- WALL HP -------------------------------------------------------------------
// upgradeBaseHP × hpGrowthPerUpgrade^upgradeLevel = max HP at that level.
// baseHP is the starting HP shown in the UI before any upgrades.
//
export const WALL = {
  baseHP: 200,               // Initial wall HP (upgrade level 0)
  upgradeBaseHP: 200,        // Base for upgrade formula: upgradeBaseHP × hpGrowthPerUpgrade^level
  hpGrowthPerUpgrade: 1.25,  // HP multiplier per wall upgrade level (+25%)
};


// ---- UNIT BUY COSTS ------------------------------------------------------------
// Base cost to buy the FIRST unit of each type.
// Each additional unit costs base × UNIT_COST_SCALE^unitsOwned.
// UNIT_COST_SCALE is 1.15 — defined in config.ts SCALING.
//
// To make a unit available earlier, lower its cost.
// To push it later, raise it. Units roughly unlock at the wave shown in comments.
//
export const UNIT_BUY_COSTS: Record<string, number> = {
  rock_thrower: 25,       // Wave 1 — first unit
  builder: 160,       // Wave 3-4 — mandatory for wall survival
  archer: 200,       // Wave 5-6
  club_swinger: 1000,       // Wave 12
  sling_thrower: 8000,       // Wave 22
  spear_thrower: 17000,       // Wave 38
  longbow_archer: 33000,       // Wave 25-35
  firefly_nest: 66000,       // Wave 40-55
  crossbow: 100000,       // Wave 35-45
  beehive_thrower: 200000,       // Wave 45-60
  crow_trainer: 400000,       // Wave 30-40
  druid: 800000,       // Wave 50-65
  trebuchet: 1600000,       // Wave 55-70
  lightning_wizard: 3200000,      // Wave 65-80
  boomerang_thrower: 5000000,      // Prestige-unlocked wall slot unit
  // Prestige-unlocked turrets
  wall_crossbow_top: 2000000,
  wall_crossbow_bottom: 25000000,
  // Skills (one-time purchase)
  poison_arrows: 1500,
  rock_rain: 8000,
  gold_rush: 25000,
  lightning_strike: 75000,
};


// ---- UNIT BASE STATS -----------------------------------------------------------
// Base combat stats for each unit. These set the floor before upgrades.
// See also: chain upgrades in config.ts which boost these values over time.
//
// To make a unit deal more base damage: raise baseDamage.
// To make it attack faster: lower attackInterval (ms between attacks).
//
export const UNIT_STATS = {
  // Ordered by unlock order (ascending buy cost)
  rock_thrower: {
    baseDamage: 3,
    attackInterval: 1154,  // ms
    projectileSpeed: 325,
    range: 300,
  },
  builder: {
    baseRepairRate: 1,
    repairPerLevel: 1,
    repairInterval: 1000,
  },
  archer: {
    baseDamage: 10,
    attackInterval: 923,
    projectileSpeed: 780,
    range: 9999,           // Full-map range
    accuracyPenalty: 0.5,  // 50% miss chance at max range
    poisonPercent: 0.15,
    poisonDuration: 3000,
    poisonDurationPerLevel: 500,
  },
  club_swinger: {
    baseDamage: 31,
    attackInterval: 1385,
    range: 60,
    aoeRadius: 40,
    aoeRadiusPerLevel: 15,
    maxTargets: 2,
    maxTargetsPerLevel: 1,
  },
  sling_thrower: {
    baseDamage: 157,
    range: 450,
    attackInterval: 2800,
    projectileSpeed: 300,
    slowEffect: 0.6,
    slowDuration: 3000,
    basePierceCount: 0,
  },
  spear_thrower: {
    baseDamage: 550,
    baseAttackInterval: 1538,
    attackSpeedReduction: 77,
    minAttackInterval: 615,
    projectileSpeed: 455,
    range: 500,
    basePierceCount: 2,
    piercePerLevel: 1,
  },
  longbow_archer: {
    baseDamage: 1000,
    range: 800,
    attackInterval: 2200,
    projectileSpeed: 600,
    accuracyPenalty: 0.5,
    basePierceCount: 0,
  },
  firefly_nest: {
    baseDamage: 1050,
    attackInterval: 3000,
    range: 500,
    projectileSpeed: 200,
    aoeRadius: 50,
    fireflyCount: 1,
    fireDuration: 2000,
    firePercent: 0.25,
  },
  crossbow: {
    baseDamage: 2000,
    attackInterval: 2000,
    range: 600,
    projectileSpeed: 550,
    basePierceCount: 1,
  },
  beehive_thrower: {
    baseDamage: 2200,
    attackInterval: 2308,
    range: 500,
    projectileSpeed: 455,
    swarmDuration: 3000,
    swarmDps: 30,
    slowEffect: 0.7,
  },
  crow_trainer: {
    baseDamage: 2500,
    attackInterval: 1923,
    range: 9999,
    projectileSpeed: 325,
  },
  druid: {
    baseDamage: 2000,
    brambleHpFactor: 2, // Multiplied by baseDamage (2000) for total HP
    brambleDpsPercent: 0.4,
    attackInterval: 3500,
    range: 400,
    brambleDuration: 10000,
    brambleSlowEffect: 0.5,
    maxBrambles: 3,
    brambleWidth: 70,
    brambleHeight: 140,
  },
  trebuchet: {
    baseDamage: 5000,
    attackInterval: 3077,
    range: 800,
    projectileSpeed: 390,
    aoeRadius: 60,
  },
  lightning_wizard: {
    baseDamage: 6000,
    attackInterval: 3500,
    range: 700,
    chainCount: 3,
    chainDamageDecay: 0.7,
  },
  boomerang_thrower: {
    baseDamage: 10000,
    attackInterval: 2200,     // ms between throws
    range: 700,               // how far the boomerang travels before returning
    projectileSpeed: 420,
    basePierceCount: 2,       // enemies hit per pass (each direction)
  },
  // Prestige-unlocked wall turrets
  wall_crossbow_top: {
    baseDamage: 10500,
    attackInterval: 1500,
    range: 600,
    projectileSpeed: 550,
    basePierceCount: 1,
  },
  wall_crossbow_bottom: {
    baseDamage: 16500,
    attackInterval: 1300,
    range: 400,
    projectileSpeed: 550,
    basePierceCount: 1,
  },
  finger: {
    baseClickDamage: 2,
  },
};


// ---- PRESTIGE ------------------------------------------------------------------
// legacyDivisor: Legacy = floor(totalGoldEarned / divisor).
// Lower value = more legacy per run (easier/faster prestige loop).
// At 1,000,000: earning 10M gold in a run gives 10 legacy.
//
export const PRESTIGE_BALANCE = {
  legacyDivisor: 1_500_000,
};


// ---- ENEMY BASE STATS ----------------------------------------------------------
// Base stats applied to every enemy before type multipliers.
// speed/speedPerWave/maxSpeed control movement. attackInterval controls wall hit rate.
//
export const ENEMY_BASE = {
  hp: ENEMY_SCALING.baseHp,
  hpPerWave: 0,        // legacy — not used
  speed: 66,
  speedPerWave: 1.162,
  maxSpeed: 150,
  damage: ENEMY_SCALING.baseDmg,
  damagePerWave: 0,    // legacy — not used
  attackInterval: 769,
};


// ---- ENEMY TYPE MULTIPLIERS ----------------------------------------------------
// attackRange: 0 = melee (walk to wall), >0 = stop that many px from wall.
// All stats = ENEMY_BASE × mult for that wave's scaling.
//
export const ENEMY_TYPES = {
  basic: {
    hpMult: 1.0,
    speedMult: 1.0,
    damageMult: 1.0,
    goldMult: 1.0,
    attackRange: 0,
  },
  brute: {
    hpMult: 2.0,
    speedMult: 0.7,
    damageMult: 1.5,
    goldMult: 1.5,
    attackRange: 0,
  },
  runner: {
    hpMult: 0.5,
    speedMult: 1.8,
    damageMult: 0.6,
    goldMult: 0.8,
    attackRange: 235,
  },
  shaman: {
    hpMult: 0.8,
    speedMult: 1.5,
    damageMult: 0.5,
    goldMult: 2.0,
    attackRange: 150,
    healPercent: 0.06,  // 6% of target's max HP per pulse
    healRange: 150,
  },
  archer: {
    hpMult: 0.6,
    speedMult: 1.0,
    damageMult: 0.8,
    goldMult: 1.2,
    attackRange: 390,
    missChance: 0.3,
  },
  shieldbearer: {
    hpMult: 1.8,
    speedMult: 0.75,
    damageMult: 1.2,
    goldMult: 1.8,
    attackRange: 0,
    rangedDamageReduction: 0.95,  // Takes only 5% of ranged damage
  },
  longbow_archer: {
    hpMult: 0.7,
    speedMult: 0.6,
    damageMult: 1.4,
    goldMult: 1.8,
    attackRange: 700,
    missChance: 0.25,
  },
  swarmling: {
    hpMult: 0.25,
    speedMult: 1.5,
    damageMult: 0.3,
    goldMult: 0.4,
    attackRange: 0,
  },
  splitter: {
    hpMult: 1.6,
    speedMult: 0.85,
    damageMult: 1.0,
    goldMult: 1.2,
    attackRange: 0,
  },
  nest_carrier: {
    hpMult: 1.4,
    speedMult: 0.6,
    damageMult: 0.4,
    goldMult: 2.5,
    attackRange: 0,
  },
  enemy_trebuchet: {
    hpMult: 1.0,
    speedMult: 0.4,
    damageMult: 3.0,
    goldMult: 2.0,
    attackRange: 600,
    wallDamageOnly: true,
  },
  boulder: {
    hpMult: 15,
    speedMult: 1.4,
    damageMult: 8,
    goldMult: 0,
    scale: 1.8,
    attackRange: 0,
  },
  saboteur: {
    hpMult: 1.0,
    speedMult: 1.2,
    damageMult: 1.5,
    goldMult: 1.0,
    attackRange: 0,
  },
  war_drummer: {
    hpMult: 2.0,
    speedMult: 0.5,
    damageMult: 0.5,
    goldMult: 2.5,
    attackRange: 0,
  },
  loot_goblin: {
    hpMult: 0.3,
    speedMult: 1.25,
    damageMult: 0,
    goldMult: 0,
    attackRange: 0,
  },
  siege_tower: {
    hpMult: 10.0,
    speedMult: 0.3,
    damageMult: 0,
    goldMult: 5.0,
    attackRange: 350, // Stays back from the wall
  },
};


// ---- BOSS DEFINITIONS ----------------------------------------------------------
// Bosses cycle every 5 waves. hpMult/dmgMult multiply the wave's base enemy stats.
//
export const BOSS_DEFINITIONS = [
  {
    name: 'Frost Warlord',
    baseType: 'brute' as const,
    scale: 1.8,
    hpMult: 18,
    dmgMult: 2.5,
    ability: 'frost' as const,
    abilityDescription: 'Freezes 2 random units for 3 seconds.',
    abilityInterval: 8000,
    attackRange: 0,
  },
  {
    name: 'Lava Titan',
    baseType: 'brute' as const,
    scale: 1.8,
    hpMult: 18,
    dmgMult: 2.0,
    ability: 'lava' as const,
    abilityDescription: 'Burns the wall for 1% of its max HP.',
    abilityInterval: 3000,
    attackRange: 0,
  },
  {
    name: 'Storm Herald',
    baseType: 'archer' as const,
    scale: 1.6,
    hpMult: 15,
    dmgMult: 2.5,
    ability: 'storm' as const,
    abilityDescription: 'Stuns all units for 2.5 seconds.',
    abilityInterval: 10000,
    attackRange: 390,
  },
  {
    name: 'Shadow Stalker',
    baseType: 'runner' as const,
    scale: 1.5,
    hpMult: 12,
    dmgMult: 3.0,
    ability: 'shadow' as const,
    abilityDescription: 'Teleports closer to the wall and gains brief invulnerability.',
    abilityInterval: 12000,
    attackRange: 235,
  },
  {
    name: 'Plague Shaman',
    baseType: 'brute' as const,
    scale: 1.6,
    hpMult: 15,
    dmgMult: 2.0,
    ability: 'plague' as const,
    abilityDescription: 'Plagues all units, reducing attack speed for 4 seconds.',
    abilityInterval: 7000,
    attackRange: 0,
  },
  {
    name: 'Iron Juggernaut',
    baseType: 'shieldbearer' as const,
    scale: 2.0,
    hpMult: 12,
    dmgMult: 3.0,
    ability: 'juggernaut' as const,
    abilityDescription: 'Gains a shield that blocks the next 8 hits.',
    abilityInterval: 20000,
    attackRange: 0,
  },
] as const;


// ---- WAVE MECHANICS ------------------------------------------------------------
// Controls enemy count, spawn speed, and gold rewards per wave.
//
export const WAVE = {
  baseEnemyCount: 5,
  enemiesPerWave: 1.2,
  bonusPerMilestone: 5,    // Extra enemies every 10 waves
  baseSpawnInterval: 1538,
  spawnDecayPerWave: 0.97, // Interval × this each wave — always faster
  minSpawnInterval: 250,
  baseGoldPerKill: ENEMY_SCALING.baseGold,
  goldPerKillPerWave: 0,   // legacy — not used
  bossGoldMultiplier: 15,
  defeatRollback: 3,
};


// ---- ELDER COUNCIL (PRESTIGE) --------------------------------------------------
// 4 elders, each with tiers. Costs are in Legacy points.
// Effect keys are read by PrestigeManager — don't rename without updating it.
//
export const ELDER_COUNCIL = {
  newbeginning: {
    color: '#44cc88',
    label: 'The New Beginning',
    tiers: [
      { name: 'It Begins', cost: 1, desc: '+10% damage, repair, wall HP, gold', effect: { damageBonusNB: 0.10, repairBonusNB: 0.10, wallHPBonusNB: 0.10, goldBonusNB: 0.10 } },
      { name: 'Idle Hands', cost: 5, desc: 'Auto-click 1/sec', effect: { autoClickInterval: 1000 } },
      { name: 'Armed Tribe', cost: 20, desc: 'Start wave 5 with army', effect: { startWave: 15, startUnits: { rock_thrower: 10, archer: 5, builder: 5, club_swinger: 5, sling_thrower: 2 } } },
      { name: 'Crossbow Outpost', cost: 50, desc: 'Unlock top wall crossbow', effect: { unlockWallCrossbowTop: true } },
      { name: 'Warband', cost: 100, desc: 'Start wave 15 with larger army', effect: { startWave: 15, startUnits: { rock_thrower: 20, archer: 10, club_swinger: 5, sling_thrower: 3, builder: 2 } } },
      { name: 'Quick Fingers', cost: 200, desc: 'Auto-click 3/sec', effect: { autoClickInterval: 333 } },
      { name: 'Siege Camp', cost: 400, desc: 'Unlock bottom crossbow. Start wave 25', effect: { startWave: 25, unlockWallCrossbowBottom: true } },
      { name: 'The Old Ways', cost: 800, desc: 'Start wave 35 with full army', effect: { startWave: 35, startUnits: { rock_thrower: 30, archer: 15, club_swinger: 8, sling_thrower: 5, spear_thrower: 3, builder: 3 } } },
      { name: 'Ancient Legion', cost: 1800, desc: 'Start wave 50 with veteran army', effect: { startWave: 50, startUnits: { rock_thrower: 50, archer: 25, club_swinger: 15, sling_thrower: 12, spear_thrower: 8, builder: 4 } } },
      { name: 'The Chosen Few', cost: 4000, desc: 'Start wave 70 with elite army', effect: { startWave: 70, startUnits: { rock_thrower: 70, archer: 40, club_swinger: 25, sling_thrower: 20, spear_thrower: 15, builder: 5 } } },
    ],
  },
  warrior: {
    color: '#cc4444',
    label: 'The Warrior',
    tiers: [
      { name: 'Sharpened Memory', cost: 3, desc: '+10% all damage', effect: { damageMult: 0.10 } },
      { name: 'Ancestral Fury', cost: 15, desc: '+20% all damage', effect: { damageMult: 0.20 } },
      { name: 'War Chants', cost: 60, desc: '+10% attack speed', effect: { attackSpeedMult: 0.10 } },
      { name: 'Bloodlust', cost: 200, desc: '+30% all damage', effect: { damageMult: 0.30 } },
      { name: 'Wrath of Ancestors', cost: 600, desc: '+25% attack speed, +30% damage', effect: { attackSpeedMult: 0.25, damageMult: 0.30 } },
      { name: 'Blood Frenzy', cost: 1500, desc: '3 kills within 1s → +20% damage for 3s', effect: { bloodFrenzy: true } },
      { name: 'Berserker Instinct', cost: 3000, desc: 'Wall below 25% HP → 2× damage for 10s (once per wave)', effect: { berserkerInstinct: true } },
    ],
  },
  mason: {
    color: '#8B6914',
    label: 'The Mason',
    tiers: [
      { name: 'Stronger Foundations', cost: 3, desc: '+25% wall HP', effect: { wallHPMult: 0.25 } },
      { name: 'Ancient Mortar', cost: 15, desc: '+50% builder repair rate', effect: { repairRateMult: 0.50 } },
      { name: 'Fortified Legacy', cost: 60, desc: 'Wall starts at upgrade level 3', effect: { startWallLevel: 3 } },
      { name: 'Thorny Walls', cost: 200, desc: 'Enemies take 2% maxHP/s at wall', effect: { thornyWallPercent: 0.02 } },
      { name: 'Boomerang Mastery', cost: 600, desc: 'Unlocks the Boomerang Thrower wall unit', effect: { unlockBoomerangThrower: true } },
      { name: 'Iron Will', cost: 1500, desc: 'Wall below 50% HP → builder repair rate doubles until above 75%', effect: { ironWill: true } },
      { name: 'Siege Master', cost: 3000, desc: '+20% damage for all wall defenders', effect: { wallSlotDamageMult: 0.20 } },
    ],
  },
  merchant: {
    color: '#daa520',
    label: 'The Merchant',
    tiers: [
      { name: 'Golden Memories', cost: 3, desc: '+15% gold from kills', effect: { goldMult: 0.15 } },
      { name: 'Head Start', cost: 15, desc: 'Start with wave-20 gold value', effect: { startGoldWaveRef: 20 } },
      { name: 'Bargain Hunters', cost: 60, desc: '-12% unit buy costs', effect: { buyCostMult: 0.88 } },
      { name: 'War Spoils', cost: 200, desc: '-15% chain upgrade costs', effect: { chainCostMult: 0.85 } },
      { name: 'Midas Touch', cost: 600, desc: 'Boss kills grant 25× gold', effect: { bossGoldMultOverride: 25 } },
      { name: 'Coin Fingers', cost: 1500, desc: '+100% gold per click', effect: { clickGoldMult: 1.0 } },
      { name: 'Windfall', cost: 3000, desc: '15% chance each kill drops 50% bonus gold', effect: { windfallChance: 0.15 } },
      { name: 'Night Watch I', cost: 4000, desc: 'Offline gold max: 6 hours', effect: { offlineHoursBonus: 3 } },
      { name: 'Night Watch II', cost: 8000, desc: 'Offline gold max: 12 hours', effect: { offlineHoursBonus: 6 } },
      { name: 'Night Watch III', cost: 15000, desc: 'Offline gold max: 18 hours', effect: { offlineHoursBonus: 6 } },
      { name: 'Night Watch IV', cost: 30000, desc: 'Offline gold max: 24 hours', effect: { offlineHoursBonus: 6 } },
    ],
  },
} as const;


// ---- ACHIEVEMENT BONUSES -------------------------------------------------------
// For each category: targets = unlock thresholds, bonusPercent = +X% per tier.
// Names, icons, and description templates stay in AchievementManager.ts.
//
// To make an achievement give more bonus: increase its bonusPercent value.
// To adjust when a tier unlocks: change the targets value.
// Tiers must stay in ascending order.
//
export const ACHIEVEMENT_BONUSES = {
  kills: {
    targets: [1, 25, 100, 500, 2500, 10000, 50000, 250000, 1000000, 5000000, 25000000],
    bonusPercent: [0.5, 0.5, 0.5, 0.75, 0.75, 1, 1.5, 2, 2.5, 3, 4],
  },
  boss_kills: {
    targets: [1, 5, 15, 50, 150, 500, 2000, 10000],
    bonusPercent: [1, 1, 1, 1.5, 2, 2.5, 3, 4],
  },
  wave: {
    targets: [5, 10, 25, 50, 75, 100, 150, 200, 300, 500],
    bonusPercent: [0.5, 0.5, 0.5, 0.75, 1, 1, 1.5, 2, 2.5, 3],
  },
  clicks: {
    targets: [50, 250, 1000, 5000, 25000, 100000, 500000, 2500000, 10000000],
    bonusPercent: [0.5, 0.5, 0.5, 0.75, 1, 1.5, 2, 2.5, 3],
  },
  gold_earned: {
    targets: [500, 5000, 50000, 500000, 5000000, 50000000, 500000000, 5000000000],
    bonusPercent: [0.5, 0.5, 0.75, 1, 1.5, 2, 2.5, 3],
  },
  gold_held: {
    targets: [500, 5000, 50000, 500000, 5000000, 50000000, 500000000],
    bonusPercent: [0.5, 0.5, 0.75, 1, 1.5, 2, 2.5],
  },
  wall_damage: {
    targets: [100, 1000, 10000, 100000, 1000000, 10000000, 100000000],
    bonusPercent: [1, 1, 1, 1.5, 2, 3, 4],
  },
  repair: {
    targets: [100, 1000, 10000, 100000, 1000000, 10000000, 100000000],
    bonusPercent: [1, 1, 1, 1.5, 2, 3, 4],
  },
  units_owned: {
    targets: [3, 10, 25, 50, 100, 200, 500],
    bonusPercent: [0.5, 0.5, 0.75, 1, 1.5, 2, 2.5],
  },
  rock_thrower_owned: {
    targets: [1, 5, 15, 50, 100, 250],
    bonusPercent: [0.25, 0.25, 0.5, 0.75, 1, 1.5],
  },
  archer_owned: {
    targets: [1, 5, 15, 50, 100, 250],
    bonusPercent: [0.25, 0.25, 0.5, 0.75, 1, 1.5],
  },
  club_swinger_owned: {
    targets: [1, 5, 15, 50, 100, 250],
    bonusPercent: [0.25, 0.25, 0.5, 0.75, 1, 1.5],
  },
  sling_thrower_owned: {
    targets: [1, 5, 15, 50, 100, 250],
    bonusPercent: [0.25, 0.25, 0.5, 0.75, 1, 1.5],
  },
  spear_thrower_owned: {
    targets: [1, 5, 15, 50, 100, 250],
    bonusPercent: [0.25, 0.25, 0.5, 0.75, 1, 1.5],
  },
  builder_owned: {
    targets: [1, 5, 15, 50, 100, 250],
    bonusPercent: [0.5, 0.5, 0.75, 1, 1.5, 2],
  },
  longbow_archer_owned: {
    targets: [1, 5, 15, 50, 100, 250],
    bonusPercent: [0.25, 0.25, 0.5, 0.75, 1, 1.5],
  },
  crow_trainer_owned: {
    targets: [1, 3, 8, 20, 50, 100],
    bonusPercent: [0.25, 0.5, 0.75, 1, 1.5, 2],
  },
  beehive_thrower_owned: {
    targets: [1, 3, 8, 20, 50, 100],
    bonusPercent: [0.25, 0.5, 0.75, 1, 1.5, 2],
  },
  trebuchet_owned: {
    targets: [1, 3, 8, 20, 50, 100],
    bonusPercent: [0.25, 0.5, 0.75, 1, 1.5, 2],
  },
  crossbow_owned: {
    targets: [1, 3, 8, 20, 50, 100],
    bonusPercent: [0.25, 0.5, 0.75, 1, 1.5, 2],
  },
  firefly_nest_owned: {
    targets: [1, 3, 8, 20, 50, 100],
    bonusPercent: [0.25, 0.5, 0.75, 1, 1.5, 2],
  },
  druid_owned: {
    targets: [1, 3, 8, 20, 50, 100],
    bonusPercent: [0.25, 0.5, 0.75, 1, 1.5, 2],
  },
  lightning_wizard_owned: {
    targets: [1, 3, 8, 20, 50, 100],
    bonusPercent: [0.25, 0.5, 0.75, 1, 1.5, 2],
  },
  boomerang_thrower_owned: {
    targets: [1, 3, 8, 20, 50, 100],
    bonusPercent: [0.25, 0.5, 0.75, 1, 1.5, 2],
  },
  prestige_count: {
    targets: [1, 3, 5, 10, 20, 50, 100, 250],
    bonusPercent: [1, 1, 1.5, 2, 2.5, 3, 4, 5],
  },
  legacy_earned: {
    targets: [5, 25, 100, 500, 2000, 10000, 50000, 250000],
    bonusPercent: [0.5, 0.5, 1, 1.5, 2, 2.5, 3, 4],
  },
  boulder_kills: {
    targets: [1, 5, 15, 50, 150, 500, 2000, 10000],
    bonusPercent: [1, 1, 1.5, 2, 2.5, 3, 4, 5],
  },
  brute_kills: {
    targets: [10, 50, 200, 1000, 5000, 25000, 100000, 500000],
    bonusPercent: [0.5, 0.5, 0.75, 1, 1.5, 2, 2.5, 3],
  },
  shaman_kills: {
    targets: [5, 25, 100, 500, 2500, 10000, 50000, 250000],
    bonusPercent: [0.5, 0.5, 0.75, 1, 1.5, 2, 2.5, 3],
  },
  skills_used: {
    targets: [10, 50, 200, 1000, 5000, 25000, 100000, 500000],
    bonusPercent: [0.5, 0.5, 0.75, 1, 1.5, 2, 2.5, 3],
  },
  click_total_damage: {
    targets: [500, 5000, 50000, 500000, 5000000, 50000000, 500000000, 5000000000],
    bonusPercent: [0.5, 0.5, 0.75, 1, 1.5, 2, 2.5, 3],
  },
  gold_single_run: {
    targets: [1000, 10000, 100000, 1000000, 10000000, 100000000, 1000000000, 10000000000],
    bonusPercent: [0.5, 0.5, 0.75, 1, 1.5, 2, 2.5, 3],
  },
  legacy_spent: {
    targets: [2, 10, 50, 200, 1000, 5000, 25000, 100000],
    bonusPercent: [0.5, 0.5, 1, 1.5, 2, 2.5, 3, 4],
  },
} as const;

export type AchievementCategory = keyof typeof ACHIEVEMENT_BONUSES;
